import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, StatusPill } from './ui'
import {
  EXCEPTION_KIND_LABELS,
  PRIORITY_LABELS,
  backofficeQueueApi,
  type ContractExceptionItem,
  type ContractPriorityInfo,
  type ContractSlaItem,
} from '../api/backofficeQueue'

/**
 * F-089 (TASK-0343, REQ-051-001 / REQ-216-022 / REQ-050-002): sulla scheda pratica, lo stato SLA ("nei tempi"
 * oppure "superato di Xh Ym"), l'elenco delle eccezioni della pratica e la priorità con la sua origine
 * (impostata a mano oppure da una regola). Tre letture indipendenti: se una manca il resto si vede lo stesso.
 * Nessun importo.
 */
export function ContractQueueStatus({ contractId }: { contractId: number }) {
  const [sla, setSla] = useState<ContractSlaItem | null>(null)
  const [exceptions, setExceptions] = useState<ContractExceptionItem[] | null>(null)
  const [priority, setPriority] = useState<ContractPriorityInfo | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setSla(null)
    setExceptions(null)
    setPriority(null)
    void Promise.allSettled([
      backofficeQueueApi.contractSla(contractId),
      backofficeQueueApi.contractExceptions(contractId),
      backofficeQueueApi.contractPriority(contractId),
    ]).then(([s, x, p]) => {
      if (cancelled) return
      if (s.status === 'fulfilled') setSla(s.value)
      if (x.status === 'fulfilled') setExceptions(x.value.exceptions)
      if (p.status === 'fulfilled') setPriority(p.value)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [contractId])

  if (loading || (!sla && !exceptions && !priority)) return null

  const sourceLabel = priority?.prioritySource === 'manual'
    ? 'impostata a mano'
    : priority?.prioritySource === 'rule'
      ? `impostata dalla regola #${priority.priorityRuleId ?? '?'}`
      : 'valore predefinito'

  return (
    <Card variant="outlined" className="mb-4">
      <CardHeader icon="schedule" title="Priorità, SLA ed eccezioni" />
      <CardContent>
        <div className="d-flex flex-wrap align-items-center gap-3 mb-2">
          {priority && (
            <span>
              Priorità: <strong>{PRIORITY_LABELS[priority.priority] ?? priority.priority}</strong>{' '}
              <span className="text-muted md-typescale-body-small">({sourceLabel})</span>
            </span>
          )}
          {sla && (
            sla.isBreached ? (
              <StatusPill tone="error" title={sla.explanation}>SLA superato di {sla.overdueLabel ?? '—'}</StatusPill>
            ) : sla.hasSla && sla.elapsedHours !== null ? (
              <StatusPill tone="success" title={sla.explanation}>SLA nei tempi</StatusPill>
            ) : (
              <span className="text-muted md-typescale-body-small">{sla.hasSla ? 'SLA: storico di stato non disponibile' : 'Stato senza SLA'}</span>
            )
          )}
        </div>

        {exceptions && (
          exceptions.length === 0 ? (
            <div className="md-typescale-body-small text-success">Nessuna eccezione sulla pratica.</div>
          ) : (
            <ul className="mb-0 ps-3 md-typescale-body-small">
              {exceptions.map((x, i) => (
                <li key={`${x.kind}-${x.ruleCode ?? ''}-${i}`}>
                  <StatusPill tone={x.severity === 'Bloccante' ? 'error' : 'warning'} className="me-1">{x.severity}</StatusPill>
                  {EXCEPTION_KIND_LABELS[x.kind]}: {x.label}
                </li>
              ))}
            </ul>
          )
        )}
      </CardContent>
    </Card>
  )
}
